import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import {
  CheckCircle2Icon,
  UndoIcon,
} from "lucide-react";
import { CreatorAction } from "@/types/types";
import { useAuth } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Frame,
  FrameFooter,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import { ArtifactThreadItem, InfoRow } from "@/components/creators-table/phase/blocks";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";
import { useDashboardStore, useEveActionsStore } from "@/stores";
import { useComplianceRole } from "@/hooks/IsAdminContext";
import ConfirmActionDialog from "./ConfirmActionDialog";

/**
 * Amy-v2 phase-review task: a script/draft/post (or custom-step) submission
 * awaits the team's verdict. Verdicts run NATIVELY against the amy
 * resolve-phase-review endpoint (the same executor the chat tool uses).
 * Layout mirrors the sibling tasks: the submission renders as an email-thread
 * style message + a Collected-Information style detail list, and the verdict
 * is the handoff-style textarea frame with the secondary actions as footer
 * links.
 */
export default function PhaseReviewAction({ action }: { action: CreatorAction }) {
  const { getToken } = useAuth();
  const openAmy = useAmyDeepLink();
  const { resolvePhaseReview, isLoading } = useEveActionsStore();
  const [notes, setNotes] = useState("");
  const label = action.phase?.label ?? action.phase?.key ?? "submission";
  // Compliance reviewers verdict here and nowhere else — no Amy chat, no
  // campaign pages (both routes are blocked for them anyway).
  const { isCompliance } = useComplianceRole();

  const artifact = action.artifact;

  const runVerdict = async (decision: "approve" | "revision") => {
    const token = await getToken();
    if (!token) return false;
    const ok = await resolvePhaseReview(action._id, decision, notes, token);
    if (ok) {
      useDashboardStore.setState((state) => ({
        tasks: state.tasks.filter((task) => task._id !== action._id),
      }));
    }
    return ok;
  };

  // The notes the creator will read, previewed in both confirmations (the
  // phase dialog's flat-frame section shape).
  const notesPreview = notes.trim() ? (
    <Frame>
      <FrameHeader>
        <FrameTitle>Your feedback to the creator</FrameTitle>
      </FrameHeader>
      <FramePanel className="p-0">
        <p className="whitespace-pre-wrap px-4 py-3 text-sm">{notes.trim()}</p>
      </FramePanel>
    </Frame>
  ) : null;

  return (
    <div className="flex flex-col items-start gap-4 animate-in fade-in-0 slide-in-from-top-2 duration-250">
      <p className="text-sm text-muted-foreground">
        {action.message ??
          `A ${label} submission from @${action.username} is waiting for a verdict.`}
      </p>

      {artifact && (
        <Frame className="w-full">
          <FrameHeader>
            <FrameTitle>Submission</FrameTitle>
          </FrameHeader>
          <FramePanel className="flex flex-col p-0 overflow-hidden">
            {/* Email-thread style message — the SAME shared item the phase
                dialog renders (excerpt, links, watch-status banners, assist
                recommendation, live-post checks: nothing dropped). */}
            <ArtifactThreadItem
              artifact={artifact}
              label={label}
              username={action.username}
              avatar={action.avatar}
            />

            {/* Collected-Information style detail rows */}
            <div className="flex flex-col divide-y border-t">
              <InfoRow label="Phase">
                <span className="capitalize">{label}</span>
              </InfoRow>
              <InfoRow label="Revisions">
                {(action.revision_count ?? 0) > 0 ? (
                  action.revision_count
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </InfoRow>
            </div>
          </FramePanel>
        </Frame>
      )}

      {/* Handoff-style verdict frame */}
      <Frame className="w-full">
        <FrameHeader>
          <FrameTitle>Verdict</FrameTitle>
        </FrameHeader>
        <FramePanel className="relative p-0 border-0">
          <Textarea
            placeholder="Feedback for the creator."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            className="min-h-24 resize-none pb-12 focus-visible:ring-indigo-500/20 focus-visible:border-indigo-500"
          />
          <div className="absolute bottom-2 right-2 flex gap-2">
            <ConfirmActionDialog
              trigger={
                <Button
                  variant="warning-ghost"
                  size="sm"
                  disabled={isLoading || !notes.trim()}
                >
                  {isLoading ? <Spinner /> : <UndoIcon />}
                  Request revision
                </Button>
              }
              title={`Send this ${label} back for revision?`}
              description={`Amy emails @${action.username} your feedback and asks for a revised ${label}.`}
              confirmLabel="Request revision"
              confirmIcon={<UndoIcon />}
              confirmVariant="warning"
              isLoading={isLoading}
              onConfirm={() => runVerdict("revision")}
            >
              {notesPreview}
            </ConfirmActionDialog>
            <ConfirmActionDialog
              trigger={
                <Button
                  variant="neutral"
                  size="sm"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner />
                  ) : (
                    <CheckCircle2Icon />
                  )}
                  Approve
                </Button>
              }
              title={`Approve this ${label}?`}
              description={`@${action.username} moves to the next step; Amy emails them the approval${notes.trim() ? " with your feedback" : ""}.`}
              confirmLabel="Approve"
              confirmIcon={<CheckCircle2Icon />}
              isLoading={isLoading}
              onConfirm={() => runVerdict("approve")}
            >
              {notesPreview}
            </ConfirmActionDialog>
          </div>
        </FramePanel>
        <FrameFooter className="flex items-center justify-between gap-2">
          <span>Feedback is required for a revision, optional for an approval.</span>
          {!isCompliance && (
            <span className="flex gap-3">
              <span
                onClick={() =>
                  openAmy(
                    `Let's discuss the ${label} submission from @${action.username} on campaign "${action.campaign_name}" (campaign_creator_id ${action._id}, campaign_id ${action.campaign_id}).`
                  )
                }
                className="text-foreground underline cursor-pointer hover:decoration-2 underline-offset-2"
              >
                Discuss with Amy
              </span>
            </span>
          )}
        </FrameFooter>
      </Frame>
    </div>
  );
}
